/**
 * Quote Repository - Handles API calls for quote operations
 */

import api from "../../../shared/api.jsx";

/**
 * Create a new quote
 * @param {Object} quoteData - Quote data
 * @param {Object} options - Options including skipAuth for public requests
 * @returns {Promise<Object>} Creation result
 */
export const createQuote = async (quoteData, options = {}) => {
  try {
    const { skipAuth = false } = options;
    const endpoint = skipAuth ? "/quotes/customer/request" : "/quotes/create";
    const response = await api.post(endpoint, quoteData, {
      skipAuth,
      skipAuthRedirect: true,
    });
    return {
      success: true,
      data: response.data,
      quoteId: response.data?.quoteId || response.data?.id,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message,
      details: error.response?.data?.details,
    };
  }
};

/**
 * Send WhatsApp message
 * @param {Object} messageData - Message data
 * @returns {Promise<Object>} Send result
 */
export const sendWhatsAppMessage = async (messageData) => {
  try {
    const response = await api.post("/api/messaging/send", messageData);
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message,
    };
  }
};

/**
 * Fetch quotes with filtering
 * @param {Object} filters - Filter criteria
 * @returns {Promise<Object>} Quotes data
 */
export const fetchQuotes = async (filters = {}) => {
  try {
    const response = await api.get("/quotes", { params: filters });
    return {
      success: true,
      data: response.quotes || response.data || [],
      count: response.count || 0,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message,
      data: [],
      count: 0,
    };
  }
};

/**
 * Fetch quote by ID
 * @param {string} quoteId - Quote identifier
 * @returns {Promise<Object>} Quote data
 */
export const fetchQuote = async (quoteId) => {
  try {
    const response = await api.get(`/quotes/${quoteId}`);
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message,
      data: null,
    };
  }
};

/**
 * Update quote status
 * @param {string} quoteId - Quote identifier
 * @param {Object} updateData - Update data
 * @returns {Promise<Object>} Update result
 */
export const updateQuoteStatus = async (quoteId, updateData) => {
  try {
    const response = await api.patch(
      `/quotes/update/${quoteId}/status`,
      updateData
    );
    return {
      success: true,
      data: response.data.data || response.data,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message,
    };
  }
};

/**
 * Send quote to customer
 * @param {string} quoteId - Quote identifier
 * @param {Object} sendData - Send data
 * @returns {Promise<Object>} Send result
 */
export const sendQuote = async (quoteId, sendData) => {
  try {
    const response = await api.post(`/quotes/send`, {
      quoteId,
      ...sendData,
    });
    if (response.success === false) {
      return {
        success: false,
        error: response.error || response.message || "Send failed",
      };
    }
    return {
      success: true,
      data: response.data,
      queued: response.queued,
      undoUntil: response.undoUntil,
      message: response.message,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message,
    };
  }
};

/**
 * Cancel a queued initial quote send (undo window before WhatsApp + email go out).
 */
export const cancelQueuedQuoteSend = async (quoteId) => {
  try {
    const response = await api.post(
      `/quotes/followup-automation/${quoteId}/cancel-initial-send`,
      {}
    );
    if (response.success === false) {
      return {
        success: false,
        error: response.error || response.message || "Cancel failed",
        alreadySent: response.alreadySent,
      };
    }
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error:
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message,
      alreadySent: error.response?.status === 409,
    };
  }
};
